import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../api/client";
import AccountSettingSelect from "../../../components/AccountSettingSelect";
import { renderEditionOption } from "../../../components/dropdownOptionRenderers";
import normalizeCondition from "../../../utils/normalizeCondition";
import TraderModal from "./TraderModal";
import { canChooseFoilOption, isFoilOptionDisabled, normalizeFoilSelection } from "./foilOptionRules";
import "./TraderCardModalLayout.css";
import {
  WANTS_ANY_EDITION_VALUE,
  WANTS_ANY_VALUE,
  WANTS_BOOLEAN_OPTIONS,
  WANTS_CONDITION_OPTIONS,
  WANTS_LANGUAGE_OPTIONS,
  getConditionColor,
  getLanguageCountryCode,
  getWantsConditionLabel,
  getWantsLanguageLabel
} from "../../../wantsSettingsOptions";

const BINARY_BOOLEAN_OPTIONS = WANTS_BOOLEAN_OPTIONS.filter((option) => option.value !== WANTS_ANY_VALUE);

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getPrintingReleaseTime(printing) {
  const timestamp = Date.parse(printing?.releasedAt || "");
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

function getRepresentativePrintingPrice(printing, { requireFoil = false } = {}) {
  const rawPrice = requireFoil ? printing?.priceFoil : printing?.priceTrend;

  if (rawPrice === null || rawPrice === undefined) {
    return null;
  }

  if (typeof rawPrice === "string" && rawPrice.trim() === "") {
    return null;
  }

  const numericPrice = Number(rawPrice);

  return Number.isFinite(numericPrice) && numericPrice >= 0 ? numericPrice : null;
}

function selectRepresentativePrinting(printings, { requireFoil = false } = {}) {
  if (!Array.isArray(printings) || printings.length === 0) {
    return null;
  }

  const eligiblePrintings = requireFoil
    ? printings.filter((printing) => Boolean(printing?.foil))
    : printings;
  const candidatePrintings = eligiblePrintings.length > 0 ? eligiblePrintings : printings;
  const pricedPrintings = candidatePrintings.filter((printing) => getRepresentativePrintingPrice(printing, { requireFoil }) !== null);

  if (pricedPrintings.length === 0) {
    return candidatePrintings[0] || null;
  }

  return [...pricedPrintings].sort((left, right) => {
    const priceDelta = getRepresentativePrintingPrice(left, { requireFoil }) - getRepresentativePrintingPrice(right, { requireFoil });

    if (priceDelta !== 0) {
      return priceDelta;
    }

    const releaseDelta = getPrintingReleaseTime(left) - getPrintingReleaseTime(right);

    if (releaseDelta !== 0) {
      return releaseDelta;
    }

    const collectorDelta = normalizeLookupValue(left?.collectorNumber).localeCompare(normalizeLookupValue(right?.collectorNumber));

    if (collectorDelta !== 0) {
      return collectorDelta;
    }

    return normalizeLookupValue(left?.setCode).localeCompare(normalizeLookupValue(right?.setCode));
  })[0] || candidatePrintings[0] || null;
}

function getMatchingPrinting(card, printings) {
  const normalizedEdition = normalizeLookupValue(card.edition);
  const normalizedCollectorNumber = normalizeLookupValue(card.collector_number);

  return printings.find((printing) => (
    normalizeLookupValue(printing.setCode) === normalizedEdition
      && normalizeLookupValue(printing.collectorNumber) === normalizedCollectorNumber
  )) || printings.find((printing) => normalizeLookupValue(printing.setCode) === normalizedEdition)
    || printings.find((printing) => (
      normalizeLookupValue(printing.edition) === normalizedEdition
        && normalizeLookupValue(printing.collectorNumber) === normalizedCollectorNumber
    ))
    || printings.find((printing) => normalizeLookupValue(printing.edition) === normalizedEdition)
    || null;
}

function getInitialFormState(card, pendingEdit = {}) {
  const availableQty = Math.max(1, Number(card?.qty) || 1);

  return {
    qty: Math.max(1, Number(pendingEdit.qty) || availableQty),
    edition: pendingEdit.edition ?? card?.edition ?? "",
    scryfallId: pendingEdit.scryfallId ?? "",
    language: pendingEdit.language ?? card?.language ?? "en",
    condition: pendingEdit.condition ?? normalizeCondition(card?.condition) ?? "NM",
    foil: pendingEdit.foil ?? Boolean(card?.foil),
    altered: pendingEdit.altered ?? Boolean(card?.altered),
    signed: pendingEdit.signed ?? Boolean(card?.signed),
    anyEdition: pendingEdit.anyEdition ?? Boolean(card?.any_edition ?? card?.anyEdition),
    anyLanguage: pendingEdit.anyLanguage ?? Boolean(card?.any_language ?? card?.anyLanguage),
    anyCondition: pendingEdit.anyCondition ?? Boolean(card?.any_condition ?? card?.anyCondition),
    anyFoil: pendingEdit.anyFoil ?? Boolean(card?.any_foil ?? card?.anyFoil),
    anyAltered: pendingEdit.anyAltered ?? Boolean(card?.any_altered ?? card?.anyAltered),
    anySigned: pendingEdit.anySigned ?? Boolean(card?.any_signed ?? card?.anySigned)
  };
}

function getWantBooleanSelectValue(value, anyValue) {
  return anyValue ? WANTS_ANY_VALUE : String(Boolean(value));
}

function getWantBooleanFormState(value) {
  if (value === WANTS_ANY_VALUE) {
    return { anyValue: true, boolValue: false };
  }

  return {
    anyValue: false,
    boolValue: value === "true"
  };
}

function renderLanguageValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any language</span>;
  }

  const countryCode = getLanguageCountryCode(value);

  return (
    <>
      <span className={`fi fi-${countryCode} account-setting-select-preview-flag`} aria-hidden="true" />
      <span className="account-setting-select-preview-label">{getWantsLanguageLabel(value)}</span>
    </>
  );
}

function renderConditionValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any condition</span>;
  }

  const conditionCode = String(value || "NM").toUpperCase();

  return (
    <>
      <span
        className="account-setting-select-preview-badge"
        style={{
          background: getConditionColor(conditionCode),
          color: conditionCode === "GD" ? "#111" : "#fff"
        }}
      >
        {conditionCode}
      </span>
      <span className="account-setting-select-preview-label">{getWantsConditionLabel(conditionCode)}</span>
    </>
  );
}

function renderBooleanValue(value, options) {
  return <span className="account-setting-select-preview-label">{options.find((option) => option.value === value)?.label || value}</span>;
}

export default function EditCardModal({ isOpen, card, pendingEdit, onClose, onApply, userId, title = "Edit Card", searchApiBasePath = "/api/traders", enableWantWildcards = false }) {
  const [printings, setPrintings] = useState([]);
  const [setMetadata, setSetMetadata] = useState({});
  const [form, setForm] = useState(getInitialFormState(card, pendingEdit));
  const [isLoadingPrintings, setIsLoadingPrintings] = useState(false);
  const [printingsError, setPrintingsError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cardName = card?.card || card?.name || "";

  useEffect(() => {
    if (!isOpen || !card) {
      return;
    }

    setForm(getInitialFormState(card, pendingEdit));
    setPrintings([]);
    setPrintingsError("");
    setSubmitError("");
    setIsSubmitting(false);
    setIsLoadingPrintings(true);

    let isCancelled = false;

    apiFetch(`${searchApiBasePath}/search/prints?card=${encodeURIComponent(cardName)}`, { userId })
      .then(async (response) => {
        const json = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(json.error || "Could not load card editions");
        }

        return Array.isArray(json.prints) ? json.prints : [];
      })
      .then((nextPrintings) => {
        if (isCancelled) {
          return;
        }

        setPrintings(nextPrintings);

        const currentState = getInitialFormState(card, pendingEdit);
        const matchingPrinting = currentState.scryfallId
          ? nextPrintings.find((printing) => printing.scryfallId === currentState.scryfallId) || getMatchingPrinting(card, nextPrintings)
          : getMatchingPrinting(card, nextPrintings);

        setForm((current) => ({
          ...current,
          edition: current.edition || matchingPrinting?.setCode || card.edition || "",
          scryfallId: current.scryfallId || matchingPrinting?.scryfallId || ""
        }));
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        setPrintingsError(error.message || "Could not load card editions");
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingPrintings(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [card, cardName, isOpen, pendingEdit, searchApiBasePath, userId]);

  const selectedPrinting = useMemo(() => {
    if (!card) {
      return null;
    }

    if (enableWantWildcards && form.anyEdition) {
      return selectRepresentativePrinting(printings, { requireFoil: form.foil && !form.anyFoil });
    }

    return printings.find((printing) => printing.scryfallId === form.scryfallId)
      || getMatchingPrinting({ ...card, edition: form.edition }, printings);
  }, [card, enableWantWildcards, form.anyEdition, form.anyFoil, form.edition, form.foil, form.scryfallId, printings]);
  const foilOptionContext = useMemo(() => ({
    selectedPrinting,
    printings,
    anyEdition: enableWantWildcards && form.anyEdition
  }), [enableWantWildcards, form.anyEdition, printings, selectedPrinting]);
  const canChooseFoil = canChooseFoilOption({
    ...foilOptionContext,
    fallbackFoil: Boolean(form.foil || card?.foil)
  });
  const previewImage = selectedPrinting?.imageNormal || selectedPrinting?.image || card?.imageNormal || card?.image || "";
  const editionSelectValue = enableWantWildcards && form.anyEdition ? WANTS_ANY_EDITION_VALUE : form.scryfallId;

  useEffect(() => {
    const setCodes = [...new Set(printings.map((printing) => normalizeLookupValue(printing.setCode)).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((current) => ({
          ...current,
          ...(data.sets || {})
        }));
      })
      .catch((error) => {
        console.error("Set metadata error:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [printings, setMetadata, userId]);

  useEffect(() => {
    const normalizedFoilState = normalizeFoilSelection({ foil: form.foil, anyFoil: form.anyFoil }, foilOptionContext);

    if (normalizedFoilState.foil !== form.foil || normalizedFoilState.anyFoil !== form.anyFoil) {
      setForm((current) => ({
        ...current,
        foil: normalizedFoilState.foil,
        anyFoil: normalizedFoilState.anyFoil
      }));
      return;
    }

    if (!canChooseFoil && form.foil) {
      setForm((current) => ({ ...current, foil: false }));
    }
  }, [canChooseFoil, foilOptionContext, form.anyFoil, form.foil]);

  if (!isOpen || !card) {
    return null;
  }

  return (
    <TraderModal isOpen={isOpen} onClose={onClose} title={title} width="860px">
      <div style={{ display: "grid", gap: "18px" }}>
        <div className={`trader-card-modal-section${previewImage ? " trader-card-modal-section--with-image" : ""}`}>
          {previewImage && (
            <div className="trader-card-modal-media">
              <div className="app-card-image-frame">
                <img
                  src={previewImage}
                  alt={cardName}
                />
              </div>
              {selectedPrinting && (
                <div className="trader-card-modal-price" style={{ fontSize: "13px", color: "#64748b" }}>
                  <div className="trader-card-modal-price-line">Trend €{selectedPrinting.priceTrend || "-"}</div>
                  <div className="trader-card-modal-price-line">Foil trend €{selectedPrinting.priceFoil || "-"}</div>
                </div>
              )}
            </div>
          )}

          <div className="trader-card-modal-fields">
            <div className="trader-card-modal-row">
              <div className="trader-card-modal-field trader-card-modal-field--qty">
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.qty}
                  onChange={(event) => {
                    const nextQty = Math.max(1, Number(event.target.value) || 1);
                    setForm((current) => ({ ...current, qty: nextQty }));
                  }}
                  className="trader-card-modal-input"
                />
              </div>

              <div className="trader-card-modal-field trader-card-modal-field--card">
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                  Card
                </label>
                <input
                  type="text"
                  value={cardName}
                  readOnly
                  className="trader-card-modal-input trader-card-modal-input--readonly"
                />
              </div>
            </div>

            <div className="trader-card-modal-row">
              <div className="trader-card-modal-field trader-card-modal-field--edition">
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                  Edition
                </label>
                <AccountSettingSelect
                  title="Edition"
                  value={editionSelectValue || ""}
                  onChange={(nextValue) => {
                    if (nextValue === WANTS_ANY_EDITION_VALUE) {
                      const preferredPrinting = selectRepresentativePrinting(printings, { requireFoil: form.foil && !form.anyFoil });

                      setForm((current) => ({
                        ...current,
                        edition: preferredPrinting?.setCode || current.edition,
                        scryfallId: preferredPrinting?.scryfallId || current.scryfallId,
                        anyEdition: true
                      }));
                      return;
                    }

                    const nextPrinting = printings.find((printing) => printing.scryfallId === nextValue);

                    setForm((current) => ({
                      ...current,
                      edition: nextPrinting?.setCode || current.edition,
                      scryfallId: nextPrinting?.scryfallId || current.scryfallId,
                      ...normalizeFoilSelection({ foil: nextPrinting?.foil ? current.foil : false, anyFoil: current.anyFoil }, {
                        selectedPrinting: nextPrinting,
                        printings,
                        anyEdition: false
                      }),
                      anyEdition: false
                    }));
                  }}
                  disabled={isLoadingPrintings || printings.length === 0}
                  options={[
                    ...(printings.length === 0 ? [{ value: "", label: "No editions found", disabled: true }] : []),
                    ...(enableWantWildcards && printings.length > 0 ? [{ value: WANTS_ANY_EDITION_VALUE, label: "Any" }] : []),
                    ...printings.map((printing) => ({
                      value: printing.scryfallId,
                      label: `${printing.edition} (${printing.setCode.toUpperCase()})${printing.collectorNumber ? ` · #${printing.collectorNumber}` : ""}`,
                      setCode: printing.setCode,
                      setMetadata: setMetadata[normalizeLookupValue(printing.setCode)],
                      showSetSymbol: true
                    }))
                  ]}
                  renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "No editions found")}
                  renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
                />
                {isLoadingPrintings && <div style={{ marginTop: "8px", fontSize: "13px", color: "#64748b" }}>Loading editions...</div>}
                {printingsError && <div style={{ marginTop: "8px", fontSize: "13px", color: "#b91c1c" }}>{printingsError}</div>}
              </div>
            </div>

            <div className="trader-card-modal-row">
              <div className="trader-card-modal-field trader-card-modal-field--half">
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                  Condition
                </label>
                <AccountSettingSelect
                  title="Condition"
                  value={enableWantWildcards && form.anyCondition ? WANTS_ANY_VALUE : form.condition}
                  options={[
                    ...(enableWantWildcards ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                    ...WANTS_CONDITION_OPTIONS
                  ]}
                  onChange={(nextValue) => setForm((current) => ({
                    ...current,
                    condition: nextValue === WANTS_ANY_VALUE ? current.condition : nextValue,
                    anyCondition: enableWantWildcards && nextValue === WANTS_ANY_VALUE
                  }))}
                  renderSelectedValue={renderConditionValue}
                  renderOption={renderConditionValue}
                />
              </div>

              <div className="trader-card-modal-field trader-card-modal-field--half">
                <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                  Language
                </label>
                <AccountSettingSelect
                  title="Language"
                  value={enableWantWildcards && form.anyLanguage ? WANTS_ANY_VALUE : form.language}
                  options={[
                    ...(enableWantWildcards ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                    ...WANTS_LANGUAGE_OPTIONS
                  ]}
                  onChange={(nextValue) => setForm((current) => ({
                    ...current,
                    language: nextValue === WANTS_ANY_VALUE ? current.language : nextValue,
                    anyLanguage: enableWantWildcards && nextValue === WANTS_ANY_VALUE
                  }))}
                  renderSelectedValue={renderLanguageValue}
                  renderOption={renderLanguageValue}
                />
              </div>
            </div>

            <div className="trader-card-modal-row">
              {enableWantWildcards ? (
                <>
                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Foil
                    </label>
                    <AccountSettingSelect
                      title="Foil"
                      value={getWantBooleanSelectValue(form.foil, form.anyFoil)}
                      options={WANTS_BOOLEAN_OPTIONS.map((option) => ({
                        ...option,
                        disabled: (option.value === "true" && !canChooseFoil)
                          || isFoilOptionDisabled(option.value, foilOptionContext)
                      }))}
                      onChange={(nextValue) => {
                        const nextState = getWantBooleanFormState(nextValue);
                        setForm((current) => ({
                          ...current,
                          foil: nextState.boolValue,
                          anyFoil: nextState.anyValue
                        }));
                      }}
                      renderSelectedValue={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                    />
                  </div>

                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Altered
                    </label>
                    <AccountSettingSelect
                      title="Altered"
                      value={getWantBooleanSelectValue(form.altered, form.anyAltered)}
                      options={WANTS_BOOLEAN_OPTIONS}
                      onChange={(nextValue) => {
                        const nextState = getWantBooleanFormState(nextValue);
                        setForm((current) => ({
                          ...current,
                          altered: nextState.boolValue,
                          anyAltered: nextState.anyValue
                        }));
                      }}
                      renderSelectedValue={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                    />
                  </div>

                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Signed
                    </label>
                    <AccountSettingSelect
                      title="Signed"
                      value={getWantBooleanSelectValue(form.signed, form.anySigned)}
                      options={WANTS_BOOLEAN_OPTIONS}
                      onChange={(nextValue) => {
                        const nextState = getWantBooleanFormState(nextValue);
                        setForm((current) => ({
                          ...current,
                          signed: nextState.boolValue,
                          anySigned: nextState.anyValue
                        }));
                      }}
                      renderSelectedValue={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, WANTS_BOOLEAN_OPTIONS)}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Foil
                    </label>
                    <AccountSettingSelect
                      title="Foil"
                      value={String(form.foil)}
                      options={BINARY_BOOLEAN_OPTIONS.map((option) => ({
                        ...option,
                        disabled: (option.value === "true" && !canChooseFoil)
                          || isFoilOptionDisabled(option.value, foilOptionContext)
                      }))}
                      onChange={(nextValue) => setForm((current) => ({ ...current, foil: nextValue === "true" }))}
                      renderSelectedValue={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                    />
                  </div>

                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Altered
                    </label>
                    <AccountSettingSelect
                      title="Altered"
                      value={String(form.altered)}
                      options={BINARY_BOOLEAN_OPTIONS}
                      onChange={(nextValue) => setForm((current) => ({ ...current, altered: nextValue === "true" }))}
                      renderSelectedValue={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                    />
                  </div>

                  <div className="trader-card-modal-field trader-card-modal-field--quarter">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Signed
                    </label>
                    <AccountSettingSelect
                      title="Signed"
                      value={String(form.signed)}
                      options={BINARY_BOOLEAN_OPTIONS}
                      onChange={(nextValue) => setForm((current) => ({ ...current, signed: nextValue === "true" }))}
                      renderSelectedValue={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                      renderOption={(value) => renderBooleanValue(value, BINARY_BOOLEAN_OPTIONS)}
                    />
                  </div>
                </>
              )}

            </div>
          </div>
        </div>

        {submitError && (
          <div style={{ padding: "12px 14px", borderRadius: "12px", background: "#fef2f2", color: "#b91c1c", fontSize: "14px" }}>
            {submitError}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{ padding: "10px 14px", borderRadius: "10px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={async () => {
              try {
                setIsSubmitting(true);
                setSubmitError("");

                await onApply({
                  qty: Math.max(1, Number(form.qty) || 1),
                  edition: selectedPrinting?.setCode || form.edition,
                  scryfallId: selectedPrinting?.scryfallId || form.scryfallId,
                  language: form.language,
                  condition: form.condition,
                  foil: form.foil,
                  altered: form.altered,
                  signed: form.signed,
                  anyEdition: form.anyEdition,
                  anyLanguage: form.anyLanguage,
                  anyCondition: form.anyCondition,
                  anyFoil: form.anyFoil,
                  anyAltered: form.anyAltered,
                  anySigned: form.anySigned
                });
              } catch (error) {
                setSubmitError(error.message || "Could not apply changes");
              } finally {
                setIsSubmitting(false);
              }
            }}
            style={{ padding: "10px 16px", borderRadius: "10px", border: "none", background: "#1976d2", color: "#fff", cursor: isSubmitting ? "wait" : "pointer", fontWeight: 600, opacity: isSubmitting ? 0.7 : 1 }}
          >
            {isSubmitting ? "Applying..." : "Apply changes"}
          </button>
        </div>
      </div>
    </TraderModal>
  );
}